import {mapChatMessagesToApiHistory} from '../../aiMessageGenerator/utils/types';
import {getAiAgentContext, sendAiAgentMessage} from '../../../redux/aiAgent/aiAgentActions';
import setGlobalState from '../../../redux/setGlobalState';
import store from '../../../store';
import {applyAgentUiAction, isNewEventFormOpenInUi} from './applyAgentUiAction';
import {
    AI_AGENT_MAX_IMAGE_BYTES,
    fileToDataUrl,
    isAiAgentVisionImage,
} from './fileToDataUrl';
import {getEffectiveSessionIntent, getEventTypeIntent, getPayloadIntent} from './agentSchema';
import {detectChatLangFromText, shouldLockChatLang} from './detectChatLang';
import type {AiAgentSubmitHandler} from '../types';
import type {AiAgentAsk, AiAgentChatLang, AiAgentUiAction} from '../../../redux/aiAgent/aiAgent.type';
import type {ChatAskPrompt} from '../../aiMessageGenerator/utils/types';

type NavigateFn = (to: string | number, options?: {state?: unknown}) => void;

type AiAgentSubmitOptions = {
    imageTooLarge: string;
    imageUnsupported: string;
    actionFailed: string;
    clearingFeeRequired: string;
    ensureClearingFeeAcknowledged: () => Promise<boolean>;
};

/**
 * An event type picked from an ask is only echoed back by the model on its next OPEN_UI,
 * so remember it now — a FILL_FORM that arrives first still needs it to open the wizard.
 * Do not treat ticket (or other) ask options as event types.
 */
function rememberEventTypeFromPrompt(prompt: string, pendingAsk: ChatAskPrompt | null): void {
    const eventTypeIntent = getEventTypeIntent();
    if(!eventTypeIntent || !pendingAsk?.options?.length) {
        return;
    }
    const locked = getEffectiveSessionIntent();
    if(locked && locked !== eventTypeIntent) {
        return;
    }
    const fromAsk = pendingAsk.options.find(opt => opt.value === prompt.trim())?.value;
    if(fromAsk) {
        setGlobalState('aiAgent.sessionEventType', fromAsk);
    }
}

/** Clearing fee before a CREATE_EVENT ask or opening the new-event wizard. */
function needsClearingFee(
    ask: AiAgentAsk | undefined | null,
    uiAction: AiAgentUiAction,
    wizardAlreadyOpen: boolean,
): boolean {
    const eventTypeIntent = getEventTypeIntent();
    if(!eventTypeIntent || getPayloadIntent(uiAction.payload) !== eventTypeIntent) {
        return false;
    }
    return !!ask?.options?.length
        || (!wizardAlreadyOpen && !!uiAction.payload?.eventType);
}

export function createAiAgentSubmitHandler(
    navigate: NavigateFn,
    options: AiAgentSubmitOptions,
): AiAgentSubmitHandler {
    return async (prompt, history, sessionId, context) => {
        rememberEventTypeFromPrompt(prompt, context.pendingAsk);

        const {eid, pid, scope, currentRoute, formId, eventType, sessionIntent, currentFormValues} = getAiAgentContext();
        if(!eid && !pid) {
            return null;
        }

        const attachment = context.attachment ?? null;
        let imageDataUrl: string | undefined;
        if(attachment?.file) {
            if(!isAiAgentVisionImage(attachment.file)) {
                return {text: options.imageUnsupported};
            }
            if(attachment.file.size > AI_AGENT_MAX_IMAGE_BYTES) {
                return {text: options.imageTooLarge};
            }
            try {
                imageDataUrl = await fileToDataUrl(attachment.file);
            } catch {
                return {text: options.imageUnsupported};
            }
        }

        const trimmedPrompt = prompt.trim();
        if(!trimmedPrompt && !imageDataUrl) {
            return null;
        }

        let chatLang: AiAgentChatLang | null = store.getState().aiAgent?.chatLang ?? null;
        if(!chatLang && shouldLockChatLang(trimmedPrompt, context.pendingAsk)) {
            chatLang = detectChatLangFromText(trimmedPrompt);
            setGlobalState('aiAgent.chatLang', chatLang);
        }

        const agentPayload = await sendAiAgentMessage({
            prompt: trimmedPrompt,
            history: mapChatMessagesToApiHistory(history),
            sessionId,
            scope,
            currentRoute,
            eid,
            pid,
            formId,
            eventType,
            sessionIntent,
            chatLang,
            currentFormValues,
            imageDataUrl,
        });

        if(!agentPayload) {
            return null;
        }

        // The server guards intent/scope/session isolation — the uiAction arrives ready to run.
        const {uiAction} = agentPayload;

        if(needsClearingFee(agentPayload.ask, uiAction, isNewEventFormOpenInUi())) {
            const acknowledged = await options.ensureClearingFeeAcknowledged();
            if(!acknowledged) {
                return {text: options.clearingFeeRequired};
            }
        }

        let actionFailed = false;
        try {
            await applyAgentUiAction({uiAction, navigate, eid});
        } catch {
            actionFailed = true;
        }

        const ask = actionFailed ? undefined : agentPayload.ask;
        let text = agentPayload.text || '';
        if(actionFailed) {
            text = text.trim()
                ? `${text.trim()}\n\n${options.actionFailed}`
                : options.actionFailed;
        }

        return {
            text,
            ...(ask ? {ask} : {}),
        };
    };
}
